import { useState } from 'react';
import TumblerAuthBoundary from './TumblerAuthBoundary';
import { Helmet } from 'react-helmet';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { Box, Button, Paper, Stack, Tab, Tabs, Typography } from '@mui/material';
import useScenarioList from './scheduling/hooks/useScenarioList';
import ScenarioList from './scheduling/ScenarioList';
import ScenarioCreate from './scheduling/ScenarioCreate';
import ScenarioDetail from './scheduling/ScenarioDetail';
import DispatchBoard from './scheduling/DispatchBoard';
import ColdRoomInventory from './scheduling/ColdRoomInventory';
import ExceptionsPanel from './scheduling/ExceptionsPanel';
import SchedulerSettings from './scheduling/SchedulerSettings';
import ResourceSettings from './scheduling/ResourceSettings';
import RevisionHistory from './scheduling/RevisionHistory';
import { useSelector } from 'react-redux';

// Workspace shell for Tumbler Scheduling. Kept at the existing route
// (":bu/:plant/:factory/Tumbler Scheduling") so existing bookmarks/links keep working
// (IMPLEMENTATION_PLAN.md Phase 2: "ใช้ route เดิมเป็น entry point"). Sub-views are switched
// with in-page tabs rather than additional router paths, since this app's routing already keys
// off literal path segments (":line"/":detail") that are awkward to extend safely without a
// router-level change outside this component's scope.
//
// Scope reset: switching plant/factory resets which scenario/tab is selected here (via `key`
// below) instead of leaving a stale selectedScenarioId pointing at another factory's scenario
// (ASSESSMENT.md P2).
export default function TumblerScheduling() {
  const { bu, plant, factory } = useParams();
  return <TumblerAuthBoundary><TumblerSchedulingWorkspace key={`${plant}/${factory}`} bu={bu} plant={plant} factory={factory} /></TumblerAuthBoundary>;
}

function TumblerSchedulingWorkspace({ bu, plant, factory }) {
  const accessToken = useSelector((state) => state.auth.access_token);
  const [tab, setTab] = useState('planning');
  const [page, setPage] = useState(1);
  const [selectedScenarioId, setSelectedScenarioId] = useState(null);

  const scenarioListState = useScenarioList({ plant, factory, accessToken, page });

  const refreshAfterChange = () => {
    scenarioListState.reload();
  };

  return (
    <Box sx={{ maxWidth: 1600, mx: 'auto', px: { xs: 2, md: 4 }, py: 3 }}>
      <Helmet><title>Tumbler Scheduling | MES</title></Helmet>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h3">Tumbler Scheduling</Typography>
          <Typography color="text.secondary">Plant {plant} / Factory {factory}</Typography>
        </Box>
        <Button component={RouterLink} to={`/${encodeURIComponent(bu)}/${encodeURIComponent(plant)}/${encodeURIComponent(factory)}/Tumbler%20Product%20Master`}>
          Open Product Master
        </Button>
      </Stack>

      <Tabs value={tab} onChange={(_, value) => setTab(value)} sx={{ mb: 3 }} variant="scrollable" scrollButtons="auto">
        <Tab value="planning" label="Planning" />
        <Tab value="dispatch" label="Dispatch" />
        <Tab value="inventory" label="Cold-room reservations" />
        <Tab value="exceptions" label="Exceptions" disabled={!selectedScenarioId} />
        <Tab value="settings" label="Scheduler settings" />
        <Tab value="resources" label="Pots & cleaning" />
        <Tab value="history" label="Activity & revisions" />
      </Tabs>

      <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, border: '1px solid', borderColor: 'divider' }}>
        {tab === 'planning' && (
          <Stack spacing={4}>
            <Box>
              <Typography variant="h5" sx={{ mb: 2 }}>Create and run schedule</Typography>
              <ScenarioCreate
                plant={plant}
                factory={factory}
                onCreated={(scenario) => { setSelectedScenarioId(scenario.id); refreshAfterChange(); }}
              />
            </Box>

            <Box>
              <Typography variant="h5" sx={{ mb: 2 }}>Scenarios</Typography>
              <ScenarioList
                state={scenarioListState}
                page={page}
                onPageChange={setPage}
                selectedId={selectedScenarioId}
                onSelect={setSelectedScenarioId}
              />
            </Box>

            <Box>
              <Typography variant="h5" sx={{ mb: 2 }}>Scenario detail</Typography>
              <ScenarioDetail key={selectedScenarioId} scenarioId={selectedScenarioId} onScenarioChanged={refreshAfterChange} onRevisionCreated={(revision) => { setSelectedScenarioId(revision.id); refreshAfterChange(); }} />
            </Box>
          </Stack>
        )}

        {tab === 'dispatch' && <DispatchBoard plant={plant} factory={factory} />}
        {tab === 'inventory' && <ColdRoomInventory plant={plant} factory={factory} />}
        {tab === 'exceptions' && (
          <ExceptionsPanel
            scenarioId={selectedScenarioId}
            onNavigateToBatch={() => setTab('planning')}
          />
        )}
        {tab === 'settings' && <SchedulerSettings plant={plant} factory={factory} />}
        {tab === 'resources' && <ResourceSettings plant={plant} factory={factory} />}
        {tab === 'history' && <RevisionHistory plant={plant} factory={factory} />}
      </Paper>
    </Box>
  );
}
